/**
 * Google Analytics 4 Integration
 * 
 * Handles GA initialization and tracking in a production-safe way.
 * Only loads GA when:
 * - Running in production environment
 * - GA ID is configured
 * - Browser environment (window exists)
 */

const GA_ID = process.env.REACT_APP_GA_ID;
const IS_PRODUCTION = process.env.NODE_ENV === 'production';

/**
 * Initialize Google Analytics
 * Should be called once when the app loads
 */
export const initGA = () => {
  // Only initialize in production with valid GA ID
  if (!IS_PRODUCTION || !GA_ID || typeof window === 'undefined') {
    console.log('[Analytics] Skipping GA initialization (not in production or no GA ID)');
    return;
  }

  // Check if gtag is already loaded
  if (window.gtag) {
    console.log('[Analytics] GA already initialized');
    return;
  }

  // Load GA script dynamically
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script1);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, {
    send_page_view: false, // We'll manually track page views
  });

  console.log('[Analytics] GA initialized:', GA_ID);
};

/**
 * Track a page view
 * @param {string} path - The page path to track (e.g., '/tutor/123')
 */
export const trackPageView = (path) => {
  if (!IS_PRODUCTION || !GA_ID || typeof window === 'undefined' || !window.gtag) {
    return;
  }

  window.gtag('config', GA_ID, {
    page_path: path,
  });

  console.log('[Analytics] Page view tracked:', path);
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event (e.g., 'contact_click')
 * @param {Object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  if (!IS_PRODUCTION || !GA_ID || typeof window === 'undefined' || !window.gtag) {
    return;
  }

  window.gtag('event', eventName, params);

  console.log('[Analytics] Event tracked:', eventName, params);
};

/**
 * Track contact button clicks
 * Helper function for specialist contact tracking
 * @param {string} contactType - Type of contact ('call', 'whatsapp', 'viber', 'telegram')
 * @param {number} specialistId - ID of the specialist
 * @param {string} specialistName - Name of the specialist
 */
export const trackContactClick = (contactType, specialistId, specialistName) => {
  trackEvent('contact_click', {
    contact_type: contactType,
    specialist_id: specialistId,
    specialist_name: specialistName,
  });
};

/**
 * Track specialist profile view
 * @param {number} specialistId - ID of the specialist
 * @param {string} specialistName - Name of the specialist
 */
export const trackSpecialistView = (specialistId, specialistName) => {
  trackEvent('view_specialist', {
    specialist_id: specialistId,
    specialist_name: specialistName,
  });
};

/**
 * Track search/filter actions
 * @param {Object} filters - Active filters
 */
export const trackSearch = (filters) => {
  trackEvent('search', {
    search_term: filters.search || '',
    subject: filters.subject || '',
    location: filters.location || '',
    mode: filters.mode || '',
  });
};
